import { configureStore, ThunkDispatch } from '@reduxjs/toolkit';
import { useDispatch } from 'react-redux';
import { AnyAction, combineReducers, EmptyObject, Middleware } from 'redux';
import { SearchState, searchReducer } from './searchSlice';
import { AppState, appReducer } from './appSlice';
import { TokenState, tokenReducer } from './tokenSlice';
import { UserState, userReducer } from './userSlice';
import { channelsState, channelsReducer } from './channelsSlice';
import { notesState, notesReducer } from './notesSlice';
import { chatsState, chatsReducer } from './chatsSlice';
import { VideoSeektimeState, videoSeektimeReducer } from './videoSeektimeSlice';
import buttonReducer, { ButtonState } from './buttonSlice';
import { resourceState, resourceReducer } from './resourceSlice';
import { peopleState, peopleReducer } from './peopleSlice';
import { workspaceState, workspaceReducer } from './workspaceSlice';
import { workspaceMembersState, workspaceMemberReducers } from './workspaceMemberSlice';
import { secondaryViewState, secondaryViewReducers } from './secondaryViewSlice';
import { secondaryViewTabState, secondaryViewTabReducers } from './secondaryVideoTabsDataSlice';
import { loadState } from './localStorage';
import { decryptedImageState, decryptedImagesReducer } from './decryptImagesSlice';
import { playlistState, playlistsReducer } from './playlistSlice';
import { channelPlaylistState, channelPlaylistReducers } from './channelPlaylist';
import { channelNoteReducers, channelNoteViewState } from './channelNoteSlice';
import { DecryptedThumbnailItemsReducer, DecryptedThumbnailItemsState } from './decryptedThumbnailItems';
import { VideoShotsReducers, VideoShotSliceState } from './shotsSlice';
import { SpeakerReducers, SpeakerSliceState } from './spearkeSlice';
import { allPlaylistsReducers, allPlaylistState } from './allPlaylists';
import { secondaryChannelVideoReducers, secondaryChannelVideoState } from './secondaryChannelVideoSlice';
import { EncryptedImagReducers, ImagesState } from './encryptedImagesSlice';
import { UserChannelStateByChannelId, UserChannelActivityReducers } from './userActivitySlice';
import { UserChannelVideoReducers, UserChannelVideoStateByChannelId } from './userChannelVideoSlice';

const reducers = combineReducers({
  app: appReducer,
  search: searchReducer,
  token: tokenReducer,
  user: userReducer,
  channels: channelsReducer,
  button: buttonReducer,
  notes: notesReducer,
  chats: chatsReducer,
  videoSeektime: videoSeektimeReducer,
  resources: resourceReducer,
  people: peopleReducer,
  workspace: workspaceReducer,
  members: workspaceMemberReducers,
  decryptedImages: decryptedImagesReducer,
  secondaryView: secondaryViewReducers,
  secondaryViewTab: secondaryViewTabReducers,
  playlists: playlistsReducer,
  channelPlaylists: channelPlaylistReducers,
  channelNotes: channelNoteReducers,
  decryptedThumbnailItems: DecryptedThumbnailItemsReducer,
  videoShots: VideoShotsReducers,
  speakers: SpeakerReducers,
  allPlaylist: allPlaylistsReducers,
  secondaryChannelVideos: secondaryChannelVideoReducers,
  encryptedImages: EncryptedImagReducers,
  userChannelActivity: UserChannelActivityReducers,
  userChannelVideo: UserChannelVideoReducers
});

const middlewareConfig = {
  serializableCheck: false
};

const store = configureStore({
  reducer: reducers,
  devTools: process.env.NODE_ENV !== 'production',
  preloadedState: loadState(),
  middleware: (getDefaultMiddleware) => {
    if (process.env.NODE_ENV !== 'production') {
      // return getDefaultMiddleware(middlewareConfig).concat(logger as Middleware);
    }
    return getDefaultMiddleware(middlewareConfig);
  }
});

export type RootState = ReturnType<typeof reducers>;

export type AppDispatch = typeof store.dispatch;
export const useAppDispatch = (): ThunkDispatch<
  EmptyObject & {
    search: SearchState;
    app: AppState;
    token: TokenState;
    user: UserState;
    channels: channelsState;
    button: ButtonState;
    notes: notesState;
    chats: chatsState;
    videoSeektime: VideoSeektimeState;
    resources: resourceState;
    people: peopleState;
    workspace: workspaceState;
    members: workspaceMembersState;
    decryptedImages: decryptedImageState;
    secondaryView: secondaryViewState;
    secondaryViewTab: secondaryViewTabState;
    playlists: playlistState;
    channelPlaylists: channelPlaylistState;
    channelNotes: channelNoteViewState;
    decryptedThumbnailItems: DecryptedThumbnailItemsState;
    videoShots: VideoShotSliceState;
    speakers: SpeakerSliceState;
    allPlaylists: allPlaylistState;
    secondaryChannelVideos: secondaryChannelVideoState;
    encryptedImage: ImagesState;
    userChannelActivity: UserChannelStateByChannelId;
    userChannelVideo: UserChannelVideoStateByChannelId;
  },
  undefined,
  AnyAction
> => useDispatch<AppDispatch>();

export { store };
